// field-visit-fe/src/services/client.js

import { ApolloClient, from } from "@apollo/client/core";
import { InMemoryCache } from "@apollo/client/cache";
import { HttpLink } from "@apollo/client/link/http";
import { onError } from "@apollo/client/link/error";
import { setContext } from "@apollo/client/link/context";
import { RestLink } from "apollo-link-rest";
import { getAuth } from "firebase/auth";
import jwtDecode from "jwt-decode";
import UserService from "../utils/userService";

const cache = new InMemoryCache();

const httpLink = new HttpLink({
  uri: `${process.env.REACT_APP_SERVER_URL}/graphql-v2`,
});

const restLink = new RestLink({
  uri: process.env.REACT_APP_NUC_URL,
});

const authLink = setContext(async (_, { headers }) => {
  let token = UserService.getFirebasetoken();
  const loginType = UserService.getLogInType();

  // Check if token has expired or is not available
  const shouldRefreshToken = !token || isTokenExpired(token);

  if (shouldRefreshToken) {
    try {
      const refreshedToken = await refreshToken();
      token = refreshedToken || "";
    } catch (error) {
      console.error("Error refreshing token:", error);
    }
  }

  return {
    headers: {
      ...headers,
      authorization: token,
      Academicyearid: 4,
      type: loginType || "",
    },
  };
});

/**
 * Error link for handling GraphQL and network errors.
 *
 * @param {Array} graphQLErrors - An array of GraphQL errors.
 * @param {Object} networkError - The network error object.
 */
const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    graphQLErrors.map(({ message, locations, path }) =>
      console.log(
        `[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`
      )
    );
  }

  if (networkError) {
    console.log(`[Network error]: ${networkError}`);
    // refresh or logout user if token is expired
    if (networkError.statusCode === 401) {
      refreshToken().then((token) => {
        if (!isTokenExpired(token)) {
          console.log(`[Network error]: screen will refresh`);
          window.location.reload();
        } else {
          UserService.handleLogout();
        }
      });
    }
  }
});

export const client = new ApolloClient({
  link: from([errorLink, authLink, restLink, httpLink]),
  cache: cache,
});

export function isTokenExpired(token) {
  if (!token) {
    return true; // Token not available, consider it expired
  }

  try {
    const decodedToken = jwtDecode(token);
    const currentTime = Math.floor(Date.now() / 1000); // Get the current time in seconds

    return decodedToken.exp < currentTime; // Compare token's expiration time with current time
  } catch (error) {
    console.error("Error decoding token:", error);
    return true; // Error occurred while decoding, consider it expired
  }
}

/**
Asynchronously refreshes the authentication token.
@returns {Promise<string>} A promise that resolves with the refreshed token.
@throws {Error} If an error occurs during the token refresh process.
*/
export async function refreshToken() {
  try {
    const idToken = await getAuth().currentUser?.getIdToken(true);
    UserService.setFirebasetoken(idToken);
    return idToken;
  } catch (error) {
    console.error("Error refreshing token:", error);
    throw error;
  }
}
